import { Component, ElementRef, OnDestroy, ViewChild, Input, EventEmitter, Output } from '@angular/core';
import {
  ContextMenuCommandId,
  create,
  createOptions,
  FileTabItemId,
  Options,
  Ribbon,
  RibbonTabType,
  RichEdit,
} from 'devexpress-richedit';
import { DxPopupComponent } from 'devextreme-angular/ui/popup';
import { RouteKeys } from '../../helper/route-keys.helper';
import { ApiService } from '../../services/api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Subscription } from 'rxjs/internal/Subscription';
import { PopupService } from '../../services/popup.service';
import { PdfJsViewerComponent } from 'ng2-pdfjs-viewer';
import notify from 'devextreme/ui/notify';
import { DocumentFormatApi } from 'devexpress-richedit/lib/client/model-api/formats/enum';

interface PreviewState {
  currentDocIndex: number;
  currentDocName: string;
  docType: string;
  previewLoaded: boolean;
}

@Component({
  selector: 'app-docX-multi-preview-popup',
  templateUrl: './docX-multi-preview-popup.component.html',
  styleUrls: ['./docX-multi-preview-popup.component.scss'],
  standalone: false,
})
export class DocxMultiPreviewPopupComponent implements OnDestroy {
  @ViewChild('popup', { static: false }) popup: DxPopupComponent;
  @ViewChild('richContainer') richContainerElement: ElementRef;
  @ViewChild('pdfViewer') pdfViewer: PdfJsViewerComponent;

  @Input() type: string = null;

  @Output() onPlatzhalter = new EventEmitter<string>();

  onClose: (e, data: any) => void;

  docs: any[] = [];
  currentDocIndex: number = 0;
  currentDocName: string = '';
  documentSelectionItems: Array<{ index: number; text: string }> = [];
  docType: string = '';
  imageSrc: any;
  textSrc: SafeHtml = '';
  fileContent: any;
  isReadOnly: boolean = true;
  isVorlage: boolean = false;
  isLoading: boolean = false;

  private rich: RichEdit;
  private previewLoadVersion: number = 0;
  private previewLoaded: boolean = false;

  subscription_get_PopupAsObservable: Subscription;

  constructor(
    private apiService: ApiService,
    private sanitizer: DomSanitizer,
    private popupService: PopupService
  ) {
    this.onClickClose = this.onClickClose.bind(this);
    this.load = this.load.bind(this);
    this.openBlob = this.openBlob.bind(this);
    this.onClickPlatzhalterliste = this.onClickPlatzhalterliste.bind(this);
    this.onClickDocumentSelection = this.onClickDocumentSelection.bind(this);
    this.insertText = this.insertText.bind(this);

    setTimeout(() => {
      if (this.type !== 'Vorlage') {
        this.subscription_get_PopupAsObservable = this.popupService.get_dokumentEditPopupAsObservable.subscribe((context) => {
          if (context !== null && context !== undefined && context.open === true) {
            if (context.data.type !== null && context.data.type !== undefined) {
              this.type = context.data.type;
            }
            this.onClose = context.closeCb;
            this.isReadOnly = context.data.readOnly;
            if (context.data.previewDokumente !== undefined) {
              this.openMulti(context.data.previewDokumente, context.data.previewIndex ?? 0, context.data.readOnly ?? true);
            } else {
              this.resetMultiPreviewState();
              if (!this.isReadOnly && context.data.doc !== undefined) {
                this.openDoc(context.data.doc);
              } else if (context.data.doc !== undefined) {
                this.load(context.data.doc);
              }
            }
          } else if (context?.open === false) {
            this.clearPreviewState();
          }
        });
      }
    }, 10);
  }

  ngOnDestroy() {
    if (this.rich) {
      this.rich.dispose();
      this.rich = null;
    }
    if (this.subscription_get_PopupAsObservable) {
      this.subscription_get_PopupAsObservable.unsubscribe();
    }
  }

  openBlob(
    blob: Blob,
    fileName: string,
    format: number,
    loadVersion: number = this.startPreviewLoad()
  ) {
    this.isLoading = true;
    this.popup.instance.show().then(() => {
      if (!this.isCurrentPreviewLoad(loadVersion)) {
        return;
      }
      if (!this.rich) {
        this.rich = create(this.richContainerElement.nativeElement, this.getOptions(fileName));
      }
      this.rich.readOnly = this.isReadOnly;
      this.rich.openDocument(blob, fileName, format, (e) => {
        if (!this.isCurrentPreviewLoad(loadVersion)) {
          return;
        }
        if (!this.isReadOnly) {
          setTimeout(() => {
            this.rich.hasUnsavedChanges = true;
          }, 50);
        }
        this.completePreviewLoad();
      });
    });
  }

  openDoc(doc: any) {
    if (doc !== null && doc !== undefined) {
      const loadVersion = this.startPreviewLoad();
      this.currentDocName = doc.docName;
      this.docType = 'doc';
      this.openBlob(doc.blob, doc.docName, DocumentFormatApi.OpenXml, loadVersion);
    }
  }

  private openPreviewBlob(blob: Blob, fileName: string, mimeType: string, loadVersion: number) {
    this.currentDocName = fileName;

    if (mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      this.docType = 'doc';
      this.openBlob(blob, fileName, DocumentFormatApi.OpenXml, loadVersion);
      return;
    }

    if (mimeType === 'application/pdf') {
      this.docType = 'pdf';
      this.popup.instance.show().then(() => {
        setTimeout(() => {
          if (!this.isCurrentPreviewLoad(loadVersion)) {
            return;
          }
          this.pdfViewer.pdfSrc = blob;
          this.pdfViewer.refresh();
          this.completePreviewLoad();
        }, 10);
      });
      return;
    }

    this.docType = null;
    this.completePreviewLoad();
    this.popup.instance.show();
  }

  openMulti(docs: any[], index: number = 0, readOnly: boolean = true) {
    if (this.docs === docs && this.previewLoaded) {
      this.isReadOnly = readOnly;
      if (this.docType === 'doc' && this.rich) {
        this.rich.readOnly = this.isReadOnly;
      }
      this.popup.instance.show();
      return;
    }

    this.previewLoadVersion++;
    this.previewLoaded = false;
    this.currentDocName = '';
    this.docs = docs || [];
    this.currentDocIndex = index;
    this.docType = '';
    this.imageSrc = null;
    this.textSrc = null;
    this.isReadOnly = readOnly;
    this.isVorlage = false;
    this.documentSelectionItems = this.docs.map((doc, docIndex) => ({
      index: docIndex,
      text: doc.bezeichnung || doc.aktenzeichen || doc.fileName || `Dokument ${docIndex + 1}`,
    }));

    if (this.docs.length > 0) {
      this.load(this.docs[index]);
    }
  }

  load(doc: any, previousState: PreviewState = null) {
    const loadVersion = this.startPreviewLoad();
    this.currentDocName = doc.fileName;
    if (doc.blob instanceof Blob) {
      this.openPreviewBlob(doc.blob, doc.fileName, doc.mimeType, loadVersion);
      return;
    }
    if (doc.previewRequest !== null && doc.previewRequest !== undefined) {
      this.apiService.postDownloadAsBlob(
        RouteKeys.Vorlage.create_stapeldruck_gesamtdokument,
        doc.fileName,
        doc.previewRequest,
        (blob) => {
          if (!this.isCurrentPreviewLoad(loadVersion)) {
            return;
          }
          doc.blob = blob;
          this.openPreviewBlob(blob, doc.fileName, doc.mimeType, loadVersion);
        },
        (err) => this.handlePreviewError(err, loadVersion, previousState)
      );
      return;
    }
    if (doc.dokumentId === null || doc.dokumentId === undefined || doc.dokumentId < 0) {
      this.isLoading = false;
      this.restorePreviewState(previousState);
      return;
    }
    if (doc.mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
      this.apiService.postDownloadAsBlob(
        RouteKeys.Dokumente.download,
        doc.fileName,
        { type: this.type, dokumentId: doc.dokumentId, fileName: doc.fileName },
        (blob) => {
          if (this.isCurrentPreviewLoad(loadVersion)) {
            this.docType = 'doc';
            this.openBlob(blob, doc.fileName, DocumentFormatApi.OpenXml, loadVersion);
          }
        },
        (err) => {
          this.handlePreviewError(err, loadVersion, previousState);
        }
      );
    } else if (doc.mimeType === 'application/msword') {
      this.apiService.postDownloadAsBlob(
        RouteKeys.Dokumente.download,
        doc.fileName,
        { type: this.type, dokumentId: doc.dokumentId, fileName: doc.fileName },
        (blob) => {
          if (this.isCurrentPreviewLoad(loadVersion)) {
            this.docType = 'doc';
            this.openBlob(blob, doc.fileName, DocumentFormatApi.Rtf, loadVersion);
          }
        },
        (err) => this.handlePreviewError(err, loadVersion, previousState)
      );
    } else if (doc.mimeType === 'application/pdf') {
      this.apiService.postDownloadAsBlob(
        RouteKeys.Dokumente.download,
        doc.fileName,
        { type: this.type, dokumentId: doc.dokumentId, fileName: doc.fileName },
        (blob) => {
          if (!this.isCurrentPreviewLoad(loadVersion)) {
            return;
          }
          this.docType = 'pdf';
          // this.popup.instance.show().then(() => {
          //   setTimeout(() => {
          //     this.pdfViewer.pdfSrc = blob;
          //     this.pdfViewer.refresh();
          //   }, 10);
          // });
          this.popup.instance.show();
          setTimeout(() => {
            if (!this.isCurrentPreviewLoad(loadVersion)) {
              return;
            }
            this.pdfViewer.pdfSrc = blob;
            this.pdfViewer.refresh();
            this.completePreviewLoad();
          }, 10);
        },
        (err) => this.handlePreviewError(err, loadVersion, previousState)
      );
    } else if (doc.mimeType.startsWith('image/')) {
      this.apiService.postDownloadAsObjectURL(
        RouteKeys.Dokumente.download,
        doc.fileName,
        { type: this.type, dokumentId: doc.dokumentId, fileName: doc.fileName },
        (objectURL) => {
          if (!this.isCurrentPreviewLoad(loadVersion)) {
            return;
          }
          this.imageSrc = this.sanitizer.bypassSecurityTrustUrl(objectURL);
          this.docType = 'image';
          this.completePreviewLoad();
          this.popup.instance.show();
        },
        (err) => this.handlePreviewError(err, loadVersion, previousState)
      );
    } else if (doc.mimeType.startsWith('text/')) {
      this.apiService.postDownloadAsBlob(
        RouteKeys.Dokumente.download,
        doc.fileName,
        { type: this.type, dokumentId: doc.dokumentId, fileName: doc.fileName },
        (blob) => {
          if (!this.isCurrentPreviewLoad(loadVersion)) {
            return;
          }
          this.docType = 'text';
          blob.text().then((x) => {
            if (!this.isCurrentPreviewLoad(loadVersion)) {
              return;
            }
            this.textSrc = this.sanitizer.bypassSecurityTrustHtml(x.replace(/(?:\r\n|\r|\n)/g, '<br>'));
            this.completePreviewLoad();
            this.popup.instance.show();
          });
        },
        (err) => this.handlePreviewError(err, loadVersion, previousState)
      );
    } else {
      this.docType = null;
      this.completePreviewLoad();
    }
  }

  onClickDocumentSelection(e) {
    const index = e?.itemData?.index;
    if (this.isLoading || typeof index !== 'number' || index < 0 || index >= this.docs.length || index === this.currentDocIndex) {
      return;
    }

    const previousState: PreviewState = {
      currentDocIndex: this.currentDocIndex,
      currentDocName: this.currentDocName,
      docType: this.docType,
      previewLoaded: this.previewLoaded,
    };

    this.currentDocIndex = index;
    this.load(this.docs[index], previousState);
  }

  onPopupShown(): void {
    if (this.docType === 'doc' && this.rich) {
      this.rich.adjust();
    }

    if (this.docType === 'pdf' && this.previewLoaded && this.pdfViewer) {
      setTimeout(() => this.pdfViewer.refresh(), 10);
    }
  }

  onClickClose() {
    this.previewLoadVersion++;
    this.fileContent = null;
    if (!this.isReadOnly && this.rich !== null && this.rich !== undefined) {
      this.rich.hasUnsavedChanges = false;
    }
    this.isLoading = false;
    this.popup.instance.hide();
  }

  private startPreviewLoad(): number {
    this.isLoading = true;
    this.previewLoaded = false;
    this.previewLoadVersion++;
    return this.previewLoadVersion;
  }

  private isCurrentPreviewLoad(loadVersion: number): boolean {
    return loadVersion === this.previewLoadVersion;
  }

  private handlePreviewError(error: any, loadVersion: number, previousState: PreviewState = null): void {
    if (!this.isCurrentPreviewLoad(loadVersion) || !error) {
      return;
    }

    this.isLoading = false;
    this.restorePreviewState(previousState);
    this.errorMessage(error);
  }

  private restorePreviewState(previousState: PreviewState): void {
    if (!previousState) {
      return;
    }

    this.currentDocIndex = previousState.currentDocIndex;
    this.currentDocName = previousState.currentDocName;
    this.docType = previousState.docType;
    this.previewLoaded = previousState.previewLoaded;
  }

  private completePreviewLoad(): void {
    this.isLoading = false;
    this.previewLoaded = this.docs.length > 0;
  }

  private clearPreviewState(): void {
    this.previewLoadVersion++;
    this.isLoading = false;
    this.fileContent = null;
    this.currentDocName = '';
    this.docType = '';
    this.imageSrc = null;
    this.textSrc = null;
    this.resetMultiPreviewState();
    this.popup.instance.hide();
  }

  private resetMultiPreviewState(): void {
    this.docs = [];
    this.currentDocIndex = 0;
    this.documentSelectionItems = [];
    this.previewLoaded = false;
  }

  getOptions(fileName: string): Options {
    const options = createOptions();
    options.width = '100%';
    options.height = '100%';
    options.readOnly = this.isReadOnly;

    const fileTab = options.ribbon.getTab(RibbonTabType.File);
    fileTab.removeItem(FileTabItemId.CreateNewDocument);
    fileTab.removeItem(FileTabItemId.OpenDocument);
    // fileTab.removeItem(FileTabItemId.ExportDocument);

    // options.ribbon.clearTabs();
    // options.ribbon.insertTab(fileTab, 0);
    options.ribbon.activeTabIndex = 0;

    this.removeContextMenuItems(options);

    options.events.saving = (s, e) => {
      e.handled = true;
      this.fileContent = e;
      if (this.fileContent !== null && this.fileContent !== undefined) {
        if (this.onClose !== undefined && this.onClose !== null) {
          this.onClose({ docContentBase64: this.fileContent.base64, docName: this.currentDocName }, true);
        }
      }
      this.popup.instance.hide();
    };
    return options;
  }

  removeContextMenuItems(options: Options) {
    for (let index = 0; index < options.contextMenu.items.length; index++) {
      const element = options.contextMenu.items[index];
      if (element.id !== ContextMenuCommandId.Copy) {
        options.contextMenu.removeItem(element);
      }
    }

    if (options.contextMenu.items.length > 1) {
      this.removeContextMenuItems(options);
    }
  }

  loadVorlage(doc: any) {
    this.isVorlage = true;
    this.currentDocName = doc.fileName;
    this.isReadOnly = false;
    if (doc.vorlagenId > 0) {
      this.docType = 'doc';
      this.apiService.postDownloadAsBlob(
        RouteKeys.Option.Vorlagenverwaltung.download,
        doc.fileName,
        { type: this.type, vorlagenId: doc.vorlagenId, fileName: doc.fileName },
        (blob) => {
          this.openBlob(blob, doc.fileName, DocumentFormatApi.OpenXml);
        }
      );
    } else {
      this.docType = null;
    }
  }

  onClickPlatzhalterliste(e) {
    this.onPlatzhalter.emit('');
  }

  private errorMessage(err: any) {
    if (err) {
      notify(
        {
          message: 'Die Datei wurde nicht gefunden!',
        },
        'error',
        4000
      );
    }
  }

  insertText(t) {
    if (this.rich !== undefined && this.rich.selection !== undefined) {
      const subDocument = this.rich.selection.activeSubDocument;
      const position = this.rich.selection.active;
      subDocument.insertText(position, t);
    }
  }
}
